import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { Button } from "@papier/ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@papier/ui/components/ui/card";
import { CheckCircleIcon, LoaderIcon } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/stripe/success")({
  component: StripeSuccessPage,
});

function StripeSuccessPage() {
  const navigate = useNavigate();
  const {
    mutateAsync: syncAfterSuccess,
    isPending: isProcessing,
    error,
  } = useMutation(
    trpc.stripe.syncAfterSuccess.mutationOptions({
      onSuccess: () => {
        toast.success("Subscription processed successfully!");
      },
      onError: (error) => {
        toast.error("Failed to process your subscription. Please contact support.", {
          description: error.message,
        });
        console.error("Failed to sync subscription:", error);
      },
    }),
  );

  useEffect(() => {
    syncAfterSuccess();
  }, []);

  const handleContinue = () => {
    navigate({ to: "/" });
  };

  if (isProcessing) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br px-4 py-12">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <LoaderIcon className="mx-auto mb-4 h-12 w-12 animate-spin text-blue-500" />
            <CardTitle>Processing Your Subscription</CardTitle>
            <CardDescription>Please wait while we set up your account...</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (error?.message) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br px-4 py-12">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-red-600">Something went wrong</CardTitle>
            <CardDescription>{error.message}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="w-full" onClick={handleContinue}>
              Continue to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br px-4 py-12">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CheckCircleIcon className="mx-auto mb-4 h-12 w-12 text-green-500" />
          <CardTitle className="text-green-600">Payment Successful!</CardTitle>
          <CardDescription>Your subscription has been activated successfully.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="rounded-lg bg-green-50 p-4">
              <p className="text-sm text-green-800">
                ✅ Payment processed
                <br />
                ✅ Account upgraded
                <br />✅ All features unlocked
              </p>
            </div>
            <Button className="w-full" onClick={handleContinue}>
              Continue to Dashboard
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
